<template>
  <div
    class="material-info"
    :class="{'hidden': isFisher && currentEffect.figure}"
  >
    <InfoTitle
      title="素材设置"
      button-text="更换素材"
      :fn="handleMaterialEdit"
    />
    <Divider />
    <InfoTitle title="素材参数" />
    <LabelSlider label="透明度" :value="effectInfo.opacity" type="opacity" />
    <LabelSlider
      label="亮度"
      :value="effectInfo.brightness"
      type="brightness"
      :disabled="isBackground"
      :step="0.01"
    />
    <LabelInput
      label="旋转"
      :deg="true"
      v-model="currentEffect.content.rotate"
      :precision="0"
      :disabled="isBackground"
    />
    <LabelInput
      label="圆角半径"
      v-model="currentEffect.content.radius"
      :precision="0"
      :disabled="isBackground"
    />
    <LabelSwitch label="阴影" v-model="currentEffect.content.shadow" />
    <template v-if="currentEffect.content.shadow">
      <div class="row-box">
        <LabelColor
          label="颜色"
          class="shadow-color"
          v-model="currentEffect.content.shadowColor"
          :showAlpha="false"
        />
        <LabelInput
          label="模糊"
          class="text-shadow-blurry"
          v-model="currentEffect.content.shadowBlur"
          :min="0"
        />
      </div>
      <div class="row-box">
        <div class="shadow-offset">
          <label>偏移</label>
        </div>
        <LabelInput
          label="x"
          class="shadow-offset-x"
          v-model="currentEffect.content.shadowX"
        />
        <LabelInput
          label="y"
          class="shadow-offset-y"
          v-model="currentEffect.content.shadowY"
        />
      </div>
    </template>
    <flip-editor
      v-model="currentEffect.content.flip"
      :disabled="isBackground"
    />
    <position-editor :effect="currentEffect" :disabled="isBackground" />
    <label-button
      :text="container ? '适应容器' : '适应画布'"
      :disabled="isBackground || (container && container.type === 'group')"
      @click="fitCanvas"
    >
      大小
    </label-button>
    <div class="setting" style="margin-top: 11px">
      <a-checkbox
        v-model="currentEffect.content.repeat"
        :disabled="isBackground"
      >
        <span class="label">平铺</span>
      </a-checkbox>
      <a-checkbox v-model="isBackground" :disabled="currentEffect.container">
        <span class="label">设为画布背景图</span>
      </a-checkbox>
    </div>
    <Divider />
    <InfoTitle title="素材编辑" />
    <label-button text="添加滤镜" @click="showFilters" :disabled="isBackground">
      滤镜
    </label-button>
    <label-button
      text="图片裁剪"
      @click="showCropModal"
      :disabled="isBackground || currentEffect.hidden"
      class="image-crop-ctrl"
    >
      裁剪
      <a-button
        class="label-button__btn"
        @click="clearImageCrop"
        :disabled="!currentEffect.content.clip"
      >
        重置
      </a-button>
    </label-button>
    <label-button
      text="画面选择"
      @click="showContainerGrids"
      :disabled="isBackground || currentEffect.container"
    >
      画中画
    </label-button>
    <label-button
      class="image-crop-ctrl"
      text="抠图"
      @click="showRemover"
      :disabled="isBackground"
    >
      绿幕抠图
      <a-button
        class="label-button__btn"
        @click="resetBackgroundRemove"
        :disabled="!currentEffect.content.originalSrc"
      >
        重置
      </a-button>
    </label-button>
    <template v-if="!currentEffect.container">
      <Divider />
      <InfoTitle title="图片转场效果" />
      <LabelInput
        label="延迟出现（秒）"
        :time="true"
        v-model="displayStart"
        :disabled="isBackground"
      />
      <LabelInput
        label="显示时长（秒）"
        :time="true"
        v-model="displayDuration"
        :disabled="isBackground"
      />
      <LabelOption
        label="入场效果"
        :optionList="enterEffectList"
        v-model="currentEffect.enter"
        :disabled="isBackground"
      />
      <LabelInput
        label="入场效果显示时长（秒）"
        :time="true"
        v-model="currentEffect.enterDuration"
        :disabled="currentEffect.enter === 'none' || isBackground"
      />
      <LabelOption
        label="出场效果"
        :optionList="leaveEffectList"
        v-model="currentEffect.leave"
        :disabled="isBackground"
      />
      <LabelInput
        label="出场效果显示时长（秒）"
        :time="true"
        v-model="currentEffect.leaveDuration"
        :disabled="currentEffect.leave === 'none' || isBackground"
      />
      <Divider />
      <InfoTitle title="图片动态效果" />
      <LabelSwitch
        label="图片动态特效"
        v-model="currentEffect.showTracks"
        :fn="handleTrackShow"
        :disabled="isBackground"
      />
      <SingleEffectEditor
        label="图片特效"
        v-for="(track, i) in currentEffect.tracks"
        :disabled="!currentEffect.showTracks || isBackground"
        :key="i"
        :track="track"
        :effectList="effectListAt(i)"
        @delete="removeTrack(i)"
        :deletable="currentEffect.tracks.length > 1"
      />
      <a-button
        v-if="currentEffect.tracks.length < 5"
        class="insert-track"
        @click="insertTrack"
        block
        :disabled="!currentEffect.showTracks || isBackground"
        icon="plus"
        >继续添加</a-button
      >
    </template>
    <div v-show="false" />

    <image-crop-modal
      :visible.sync="cropVisible"
      :src="currentEffect.content.src"
      :value="currentEffect.content.clip"
      @change="handleImageCropChange"
    />

    <container-grid-convert
      :visible.sync="containerGridVisible"
      @ok="handleContainerGridOK"
    />
    <material-bg-remover
      :visible.sync="removerVisible"
      :src="currentEffect.content.src"
      @done="handleBgRemove"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import LabelInput from "../base/LabelInput";
import LabelOption from "../base/LabelOption";
import LabelSwitch from "../base/LabelSwitch";
import LabelSlider from "../base/LabelSlider";
import LabelButton from '../base/LabelButton';
import LabelColor from "../base/LabelColor";
import InfoTitle from "../base/InfoTitle";
import Divider from '../base/Divider';
import SingleEffectEditor from '../base/SingleEffectEditor';
import PositionEditor from '../base/PositionEditor';
import FlipEditor from '../base/FlipEditor';
import ImageCropModal from './ImageCropModal';
import ContainerGridConvert from './ContainerGridConvert';
import { getImageAspectRatio, encodeURIIfNeeded } from '../../utils';
import { fitCanvas } from '../../utils/util';
import { effectParams } from '../../utils/textEffectMap';
import { containerGrids } from '../../utils/shapes';
import MaterialBgRemover from '../materialColorPicker/MaterialBgRemover.vue';

export default {
  name: "MaterialInfo",
  components: {
    LabelInput,
    LabelOption,
    LabelSwitch,
    LabelSlider,
    LabelColor,
    InfoTitle,
    Divider,
    SingleEffectEditor,
    PositionEditor,
    LabelButton,
    FlipEditor,
    ImageCropModal,
    ContainerGridConvert,
    MaterialBgRemover
  },
  data: function () {
    return {
      opacity: 1,
      light: 1,
      enterEffectList: [
        { name: '无', value: 'none' },
        { name: '淡入', value: 'enter' }
      ],
      leaveEffectList: [
        { name: '无', value: 'none' },
        { name: '淡出', value: 'enter' }
      ],
      cropVisible: false,
      containerGridVisible: false,
      removerVisible: false
    };
  },
  computed: {
    ...mapGetters(['activeFragment', 'effectInfo', 'currentEffect']),
    ...mapState(['isFisher', 'effectNames', 'generalEffects', 'screenRatio', 'effects', 'shapeNames']),
    screenWidth() {
      return 480 * this.screenRatio;
    },
    isBackground: {
      get() {
        return this.activeFragment.background === this.currentEffect.id;
      },
      set(value) {
        if (value) {
          this.$store.dispatch('setBackground');
        } else {
          this.$store.dispatch('unsetBackground');
        }
      }
    },
    displayStart: {
      get() {
        return this.currentEffect.start;
      },
      set(value) {
        this.$set(this.currentEffect, 'start', Math.max(value, 0));
      }
    },
    displayDuration: {
      get() {
        return this.currentEffect.duration;
      },
      set(value) {
        this.$set(this.currentEffect, 'duration', Math.max(value, 400));
      }
    },
    filterPreviewStyle() {
      if (this.filterParams.src) {
        return {
          backgroundImage: `url(${encodeURIIfNeeded(this.filterParams.src)})`,
          '--filter': `brightness(${this.filterParams.brightness}) blur(${this.filterParams.blur}px) contrast(${this.filterParams.contrast}%) grayscale(${this.filterParams.grayscale ? 100 : 0}%) invert(${this.filterParams.invert ? 100 : 0}%)`
        }
      }
      return {};
    },
    container() {
      const { container } = this.currentEffect;
      if (container) {
        const containerEf = this.effects[container];
        return containerEf;
      }
      return null;
    }
  },
  methods: {
    ...mapActions(['setEditStatus']),
    ...mapMutations(['handleFilterShowing']),
    onChange: function (value) {
      console.log('changed', value);
    },
    handleTrackShow(value) {
      if (value) {
        this.$set(this.currentEffect.tracks[0], 'start', this.currentEffect.start);
      }
    },
    insertTrack() {
      let effectType = '';
      for (var i of this.generalEffects) {
        if (this.currentEffect.tracks.every(tr => {
          switch (tr.effectType) {
            case 'slide':
            case 'slideHorizon':
              return i !== 'slide' && i !== 'slideHorizon';
          }
          return i !== tr.effectType;
        })) {
          effectType = i;
          break;
        }
      }
      if (effectType) {
        this.currentEffect.tracks.push({
          id: +new Date() + '',
          effectType,
          params: effectParams[effectType] ? { ...effectParams[effectType] } : null,
          start: 1e3,
          duration: 1e3
        });
      } else {
        console.log('No effect was added');
      }
    },
    removeTrack(i) {
      this.$set(
        this.currentEffect,
        'tracks',
        this.currentEffect.tracks.slice(0, i).concat(this.currentEffect.tracks.slice(i + 1))
      )
    },
    effectListAt(i) {
      return this.generalEffects
        .map(v => {
          let disabled = false;
          switch (v) {
            case 'slide':
            case 'slideHorizon':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && (tr.effectType === 'slide' || tr.effectType === 'slideHorizon'));
              break;
            case 'scale':
            case 'gradientFade':
            case 'gradientHide':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && tr.effectType === v);
              break;
          }
          return {
            name: this.effectNames[v],
            value: v,
            disabled
          };
        });
    },
    showFilters() {
      const efc = this.currentEffect.content;

      if (!efc.filterParams) {
        this.$set(efc, 'filterParams', {
          blur: 0,
          contrast: 100,
          grayscale: false,
          invert: false
        });
      }
      this.handleFilterShowing(true);
    },
    handleMaterialEdit() {
      this.handleFilterShowing(false);
      this.setEditStatus(true);
    },
    /**
     * fit the image within the canvas' size.
     */
    fitCanvas() {
      const efc = this.currentEffect.content;
      const { container } = this;
      if (container) {
        const cefc = container.content;
        let width = cefc.width;
        let height = cefc.height;
        const containerAspectRatio = width / height;
        if (!efc.repeat) {
          const effectAspectRatio = efc.width / efc.height;
          if (effectAspectRatio > containerAspectRatio) {
            width = height * effectAspectRatio;
          } else {
            height = width / effectAspectRatio;
          }
        }

        efc.width = width;
        efc.height = height;
        efc.top = (cefc.height - height) / 2;
        efc.left = (cefc.width - width) / 2;
        return;
      }
      const { top, left, width, height } = fitCanvas(efc, this.screenWidth, 480);

      efc.height = height;
      efc.width = width;
      efc.top = top;
      efc.left = left;
    },
    showCropModal() {
      if (this.currentEffect.locked) {
        this.cropVisible = true;
        return;
      }
      this.$store.commit('handleClipping', true);
      this.$store.commit('handleClippingEffect', this.currentEffect);
    },
    handleImageCropChange({ x, y, w, h }) {
      const efc = this.currentEffect.content;
      const prevClip = efc.clip || {
        w: efc.width,
        h: efc.height
      };
      this.$set(efc, 'clip', { x, y, w, h });
      if (!efc.repeat) {
        efc.height *= h / prevClip.h;
        efc.width *= w / prevClip.w;
      }
    },
    clearImageCrop() {
      const efc = this.currentEffect.content;
      if (efc.clip) {
        if (!efc.repeat) {
          getImageAspectRatio(efc.src).then(aspectRatio => {
            this.$set(efc, 'clip', null);
            efc.width = efc.height * aspectRatio;
          });
        }
      }
    },
    showContainerGrids() {
      this.containerGridVisible = true;
    },
    handleContainerGridOK(e) {
      const arr = containerGrids[e]({
        w: this.screenWidth,
        h: 480,
        shapeNames: this.shapeNames
      });
      const curId = +new Date();
      const effects = {};

      const prevEfc = this.currentEffect.content;
      const effectAspectRatio = prevEfc.width / prevEfc.height;

      const effectIds = arr.map((efc, i) => {
        const id = curId + 'g' + i;
        const cid = curId + 'c' + i;
        let width = efc.width;
        let height = efc.height;
        const containerAspectRatio = width / height;
        if (!prevEfc.repeat) {
          if (effectAspectRatio > containerAspectRatio) {
            width = height * effectAspectRatio;
          } else {
            height = width / effectAspectRatio;
          }
        }
        const childEffect = {
          ...this.currentEffect,
          id: cid,
          container: id,
          content: {
            ...this.currentEffect.content,
            top: (efc.height - height) / 2,
            left: (efc.width - width) / 2,
            width,
            height
          },
          showTracks: false
        };
        const effect = {
          ...this.currentEffect,
          id,
          type: 'container',
          locked: false,
          hidden: false,
          content: efc,
          children: [cid],
          enter: 'none',
          enterDuration: 1e3,
          leave: 'none',
          leaveDuration: 1e3,
          showTracks: false
        };
        effects[id] = effect;
        effects[cid] = childEffect;
        return id;
      });

      this.$store.commit('handleEffects', {
        ...this.effects,
        ...effects
      });

      this.activeFragment.effects = [].concat.apply([], this.activeFragment.effects.map(id => {
        if (id === this.currentEffect.id) {
          return effectIds;
        }
        return id;
      }));

      this.$store.commit('handleEffectSelect', effectIds);
      this.containerGridVisible = false;
    },
    showRemover() {
      this.removerVisible = true;
    },
    handleBgRemove(src) {
      const efc = this.currentEffect.content;
      if (!efc.originalSrc) {
        this.$set(efc, 'originalSrc', efc.src);
      }
      this.$set(efc, 'src', src);
      this.removerVisible = false;
    },
    resetBackgroundRemove() {
      const efc = this.currentEffect.content;
      this.$set(efc, 'src', efc.originalSrc);
      this.$delete(efc, 'originalSrc');
    }
  },
  beforeDestroy() {
    this.setEditStatus(false);
  },
  watch: {
    'currentEffect.content.repeat'(val) {
      if (!val) {
        const efc = this.currentEffect.content;
        if (efc.clip) {
          efc.width = efc.height * efc.clip.w / efc.clip.h;
        } else {
          getImageAspectRatio(efc.src).then(aspectRatio => {
            efc.width = efc.height * aspectRatio;
          });
        }
      }
    }
  }
}
</script>

<style scoped>
.material-info {
  padding: 11px 12px 11px 13px;
  background: rgba(248, 248, 248, 1);
}
.hidden {
  display: none;
}
.setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
}

.label {
  font-size: 12px;
  font-weight: 400;
  /* color: rgba(101, 102, 103, 1); */
}

.image-crop-ctrl ::v-deep .label-button__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: 6px;
}

.toggle-button {
  margin-top: 12px;
  height: 36px;
  border-radius: 2px;
}

.insert-track {
  border-color: #e5e5e7;
  background: #fff;
  margin-top: 10px;
  color: rgba(0, 0, 0, 0.75);
  border-radius: 2px;
}

.insert-track:hover,
.insert-track:focus {
  border-color: #ccc;
}

.insert-track:disabled {
  border-color: #dcdcdc;
  background: #fff;
  color: rgba(0, 0, 0, 0.75);
  opacity: 0.35;
}

.row-box {
  font-size: 12px;
  color: #656667;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.material-info ::v-deep .setting-input {
  margin-left: 3px;
}

.shadow-color,
.shadow-offset {
  margin-top: 10px;
}
</style>
